import React from 'react';
import classNames from 'classnames';

import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Link, Route, Switch, useParams } from 'react-router-dom';

import { BusyStore } from 'ui/base/button';
import { useStyles } from 'ui/base/theme';
import { PasswordStore } from 'ui/base/input';
import { FormSubmitEvent } from 'ui/base/types';
import { StandardPage } from 'ui/base/page/standard_page';

import { ActiveButton, DefaultButton } from 'ui/public/button';
import { LabeledInput, PasswordInput } from 'ui/public/input';

import { ErrorMessage } from 'ui/public/error_message/error_message';

import { Optional } from 'services/base/types';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';

import {
  InvoiceStripe,
  LicenseFirewall,
  Payment,
  PaymentType,
  PlanFirewallBilling,
  SubscriptionFirewall,
} from 'services/accounts/types';

import { SubscriptionFirewallService } from 'services/accounts/plans_service';

import {
  getPlanFirewallEstimatedEndDate,
  getPlanFirewallPackageName,
  getPlanFirewallPlanActivePackage,
  getPlanFirewallStripeItemLabel,
} from 'services/accounts/logic/plans/plan_firewall';

import { getPaymentMethodName } from 'services/accounts/logic/payments/payments';

import {
  flattenInvoiceItems,
  totalInvoiceItems,
} from 'services/accounts/logic/invoices/invoices';

import { formatPrice } from 'assets/enums/currencies/currencies';

import {
  themedPlanFirewallPackageIconStyles,
  getPlanFirewallPackageIcon,
} from 'assets/enums/plans/firewall/plan_firewall';

import { PlanItemFirewallBaseRequiredModalProps } from '../plan_item_firewall';

import ManagePlansMessages from '../../../../manage_plans_messages';
import PlanFirewallMessages from '../plan_item_firewall_messages';
import Messages from './plan_firewall_status_modal_messages';

type PlanItemFirewallStatusInvoiceViewModalProps = PlanItemFirewallBaseRequiredModalProps;
const PlanItemFirewallStatusInvoiceViewModal = observer(({ styles, accounts, modal }: PlanItemFirewallStatusInvoiceViewModalProps) => {
  const { invoiceID } = useParams<{ invoiceID: string }>();

  const invoice = accounts.invoices.get(invoiceID);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const nestNodeUnderList = (child: React.ReactNode, depth: number, d = 0) => {
    return d === depth ? child : <li>{nestNodeUnderList(child, depth, d + 1)}</li>;
  };

  return (
    <div className={classNames(styles.modalContainerLarge, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.invoiceViewName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        {invoice && (
          <div className={styles.modalContentRow}>
            <div className={styles.section}>
              <div className={styles.hero}>
                {invoice.settled_at ? (
                  <img className={styles.heroIcon} src={styles.heroIconImageStatusGood} alt='Good Icon' />
                ) : (
                  <img className={styles.heroIcon} src={styles.heroIconImageStatusWarn} alt='Warning Icon' />
                )}
                <div className={styles.heroHead}>
                  {formatPrice(invoice.currency, Math.max(totalInvoiceItems(invoice), 0), true)}
                </div>
                <div className={styles.heroTitle}>
                  {getPlanFirewallStripeItemLabel(invoice.label, locale)}
                </div>
                <div className={styles.heroDetail}>
                  {invoiceID}
                </div>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.field}>
                <div className={styles.fieldPart}>
                  <div className={classNames(styles.field, styles.item)}>
                    <div className={styles.fieldTextLeft} >
                      {invoice.created_at.toLocaleString(locale, format)}
                      <div className={styles.fieldInfo}>
                        {Messages.invoiceViewFieldCreatedDate()}
                      </div>
                    </div>
                    <img className={styles.fieldIcon} src={styles.fieldIconImageStatusWarn} alt='Warning Icon' />
                  </div>
                  <div className={classNames(styles.field, styles.item)}>
                    <div className={styles.fieldTextLeft} >
                      {invoice.settled_at ? invoice.settled_at.toLocaleString(locale, format)
                                          : Messages.invoiceViewStatusPending()}
                      <div className={styles.fieldInfo}>
                        {Messages.invoiceViewFieldCompletedDate()}
                      </div>
                    </div>
                    <img className={styles.fieldIcon} src={styles.fieldIconImageStatusGood} alt='Good Icon' />
                  </div>
                  {invoice.payment && (
                    <div className={classNames(styles.field, styles.item)}>
                      <div className={styles.fieldTextLeft} >
                        {getPaymentMethodName(invoice.payment)}
                        <div className={styles.fieldInfo}>
                          {Messages.invoiceViewFieldPayment()}
                        </div>
                      </div>
                      <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.section}>
              {flattenInvoiceItems(invoice).map((item, index) => (
                <div className={classNames(styles.field, styles.item)} key={`item-${index}`}>
                  <div className={styles.fieldCharge}>
                    {nestNodeUnderList(getPlanFirewallStripeItemLabel(item.label, locale), item.depth)}
                  </div>
                  <div className={styles.fieldAmount}>
                    {formatPrice(invoice.currency, item.amount, true)}
                  </div>
                </div>
              ))}
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldCharge}>
                  <strong>{Messages.invoiceViewFieldAmountTotal()}</strong>
                </div>
                <div className={styles.fieldAmount}>
                  {formatPrice(invoice.currency, totalInvoiceItems(invoice), true)}
                </div>
              </div>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldCharge}>
                  <strong>{Messages.invoiceViewFieldAmountDue()}</strong>
                </div>
                <div className={styles.fieldAmount}>
                  {formatPrice(invoice.currency, Math.max(totalInvoiceItems(invoice), 0), true)}
                </div>
              </div>
            </div>
            {(invoice.type === PaymentType.STRIPE) && (invoice as InvoiceStripe).link && (
              <div className={styles.section}>
                <a className={styles.link}
                   target='_blank' rel='noopener noreferrer'
                   href={(invoice as InvoiceStripe).link}>
                  {Messages.invoiceViewActionOpenStripe()}
                </a>
              </div>
            )}
            <DefaultButton type='button' onClick={modal.history.goBack}>
              {ManagePlansMessages.actionBack()}
            </DefaultButton>
          </div>
        )}
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallStatusInvoiceListModalProps = PlanItemFirewallBaseRequiredModalProps;
const PlanItemFirewallStatusInvoiceListModal = observer(({ styles, accounts, plan, modal, root }: PlanItemFirewallStatusInvoiceListModalProps) => {
  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
  };

  return (
    <div className={classNames(styles.modalContainerLargest, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.invoiceListName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            {accounts.invoices.filter(i => i.relations.plan_firewall_id === plan.id)
                     .sorted((a, b) => b.created_at.getTime() - a.created_at.getTime()).map(invoice => (
              <Link key={invoice.id} to={`${root}/status/invoices/${invoice.id}`}
                    className={classNames(styles.field, styles.item, styles.clickable)}>
                <div className={styles.fieldTextLeft} style={{ flex: 2 }}>
                  {invoice.created_at.toLocaleDateString(locale, format)}
                  <div className={styles.fieldInfo}>
                    {Messages.invoiceListFieldDate()}
                  </div>
                </div>
                <div className={styles.fieldTextLeft} >
                  {formatPrice(invoice.currency, Math.max(totalInvoiceItems(invoice), 0), true)}
                  <div className={styles.fieldInfo}>
                    {Messages.invoiceListFieldAmount()}
                  </div>
                </div>
                <div className={styles.fieldTextLeft} >
                  {!invoice.settled_at ? Messages.invoiceListStatusPending()
                                       : Messages.invoiceListStatusDone()}
                  <div className={styles.fieldInfo}>
                    {Messages.invoiceListFieldStatus()}
                  </div>
                </div>
                <div className={styles.open}/>
              </Link>
            ))}
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallStatusSubscriptionCancelModalProps = PlanItemFirewallBaseRequiredModalProps & {
  subscription: SubscriptionFirewall;
};
const PlanItemFirewallStatusSubscriptionCancelModal = observer(({ styles, accounts, pangolin, plan, modal, root }: PlanItemFirewallStatusSubscriptionCancelModalProps) => {
  const [busy] = React.useState(new BusyStore());
  const [passwordStore] = React.useState(new PasswordStore(''));
  const [passwordError, setPasswordError] = React.useState(' ');

  const disabled = passwordStore.value.length === 0;

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await SubscriptionFirewallService.delete(
          accounts, pangolin,
          passwordStore.value,
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.success) {
      runInAction(() => {
        const plan = accounts.pangolins.get(pangolin.id)!.plans.firewall;
        const planActivePackage = getPlanFirewallPlanActivePackage(plan);
        if (!planActivePackage) { modal.hideModal(); return; }
        while (modal.history.position > 1) modal.history.goBack();
        modal.history.replace(`${root}/status/subscription`);
      });
    } else if (result.status === 403) {
      // Display authorization error
      setPasswordError(Messages.errorAuthFailed());
    } else {
      // Display generic error
      setPasswordError(Messages.errorGeneric());
    }
  }, [modal, accounts, pangolin, root, busy, passwordStore]);

  return (
    <div className={classNames(styles.modalContainerBig, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.subscriptionCancelName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <form className={styles.modalContentRow} onSubmit={onSubmit}>
          <div className={styles.hero}>
            <img className={styles.heroIcon} src={styles.heroIconImageWarning} alt='Warning Icon' />
            <div className={styles.heroTitle}>
              {Messages.subscriptionCancelHeroTitle()}
            </div>
            <p className={styles.heroText}>
              {!plan.license ? <>
                {Messages.subscriptionCancelHeroTerminate()}
                <br/><br/>
                {Messages.subscriptionCancelHeroPassword()}
              </> : <>
                {Messages.subscriptionCancelHeroLicense()}
                <br/><br/>
                {Messages.subscriptionCancelHeroPassword()}
              </>}
            </p>
          </div>
          <LabeledInput label={ManagePlansMessages.inputPassword()}>
            <PasswordInput
              store={passwordStore}
              name='password'
              id='password'
            />
          </LabeledInput>
          <ErrorMessage message={passwordError}/>
          <ActiveButton busy={busy} disabled={disabled}>
            {ManagePlansMessages.actionConfirm()}
          </ActiveButton>
          <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </form>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallStatusSubscriptionModalProps = PlanItemFirewallBaseRequiredModalProps & {
  subscription: SubscriptionFirewall;
};
const PlanItemFirewallStatusSubscriptionModal = observer(({ styles, accounts, plan, modal, root, subscription }: PlanItemFirewallStatusSubscriptionModalProps) => {
  const packageIcons = useStyles(themedPlanFirewallPackageIconStyles);

  const payment: Optional<Payment> = plan.payment_id ? accounts.payments.get(plan.payment_id) : undefined;

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
  };

  let nextSubscription: Optional<Pick<SubscriptionFirewall, 'package' | 'valid_since' | 'valid_until'>>;
  if (plan.package && plan.billing && plan.billing !== PlanFirewallBilling.LIFETIME &&
      payment && !subscription.is_cancelled && !subscription.is_terminal) {
    nextSubscription = { package: plan.package, valid_since: subscription.valid_until,
                         valid_until: getPlanFirewallEstimatedEndDate(plan.billing, subscription.valid_until) };
  }

  const canUnsubscribe = !!nextSubscription;
  const onUnsubscribe = React.useCallback(() => {
    modal.history.push(`${root}/status/subscription/cancel`);
  }, [modal, root]);

  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.subscriptionInfoName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {Messages.subscriptionInfoCurrentName()}
            </div>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft} >
                {getPlanFirewallPackageName(subscription.package)}
                <div className={styles.fieldInfo}>
                  {Messages.subscriptionInfoFieldPackage()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Package Icon'
                   src={getPlanFirewallPackageIcon(packageIcons, subscription.package)} />
            </div>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft} >
                {subscription.valid_since.toLocaleDateString(locale, format)} &mdash; {subscription.valid_until.toLocaleDateString(locale, format)}
                <div className={styles.fieldInfo}>
                  {Messages.subscriptionInfoFieldPeriod()}
                </div>
              </div>
              <img className={styles.fieldIcon} src={styles.fieldIconImageDuration} alt='Duration Icon' />
            </div>
          </div>
          {nextSubscription && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {Messages.subscriptionInfoOnRenewName()}
              </div>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldTextLeft} >
                  {getPlanFirewallPackageName(nextSubscription.package)}
                  <div className={styles.fieldInfo}>
                    {Messages.subscriptionInfoFieldPackage()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Package Icon'
                    src={getPlanFirewallPackageIcon(packageIcons, nextSubscription.package)} />
              </div>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldTextLeft} >
                  {nextSubscription.valid_since.toLocaleDateString(locale, format)} &mdash; {nextSubscription.valid_until.toLocaleDateString(locale, format)}
                  <div className={styles.fieldInfo}>
                    {Messages.subscriptionInfoFieldPeriod()}
                  </div>
                </div>
                <img className={styles.fieldIcon} src={styles.fieldIconImageDuration} alt='Duration Icon' />
              </div>
            </div>
          )}
          {subscription.is_terminal && (
            <div className={styles.section}>
              <p className={classNames(styles.notes, styles.warning)}>
                {Messages.subscriptionInfoWarningTerminal()}
              </p>
            </div>
          )}
          {!subscription.is_terminal && !payment && (
            <div className={styles.section}>
              <p className={classNames(styles.notes, styles.warning)}>
                {Messages.subscriptionInfoWarningPayment()}
              </p>
            </div>
          )}
          {subscription.invoice_id && (
            <div className={styles.section}>
              <Link className={styles.link} to={`${root}/status/invoices/${subscription.invoice_id}`}>
                {Messages.actionInvoiceView()}
              </Link>
            </div>
          )}
          <ActiveButton onClick={onUnsubscribe} disabled={!canUnsubscribe}>
            {ManagePlansMessages.actionUnsubscribe()}
          </ActiveButton>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallStatusLicenseModalProps = PlanItemFirewallBaseRequiredModalProps & {
  license: LicenseFirewall;
};
const PlanItemFirewallStatusLicenseModal = observer(({ styles, plan, modal, root, license }: PlanItemFirewallStatusLicenseModalProps) => {
  const packageIcons = useStyles(themedPlanFirewallPackageIconStyles);
  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.licenseInfoName()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft} >
                {getPlanFirewallPackageName(license.package)}
                <div className={styles.fieldInfo}>
                  {Messages.licenseInfoFieldPackage()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Package Icon'
                   src={getPlanFirewallPackageIcon(packageIcons, license.package)} />
            </div>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft} >
                {Messages.valueLifetime()}
                <div className={styles.fieldInfo}>
                  {Messages.licenseInfoFieldPeriod()}
                </div>
              </div>
              <img className={styles.fieldIcon} src={styles.fieldIconImageDuration} alt='Duration Icon' />
            </div>
          </div>
          {plan.subscription && plan.subscription?.package >= license.package && (
            <div className={styles.section}>
              <p className={classNames(styles.notes, styles.warning)}>
                {Messages.licenseInfoWarningEffectivity()}
              </p>
            </div>
          )}
          {license.invoice_id && (
            <div className={styles.section}>
              <Link className={styles.link} to={`${root}/status/invoices/${license.invoice_id}`}>
                {Messages.actionInvoiceView()}
              </Link>
            </div>
          )}
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallStatusHomeModalProps = PlanItemFirewallBaseRequiredModalProps;
const PlanItemFirewallStatusHomeModal = observer(({ styles, accounts, plan, store, modal, root }: PlanItemFirewallStatusHomeModalProps) => {
  const payment: Optional<Payment> = plan?.payment_id ? accounts.payments.get(plan.payment_id) : undefined;

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
  };

  const activePackage = getPlanFirewallPlanActivePackage(plan);
  const activePackageName = activePackage ? getPlanFirewallPackageName(activePackage) : '';

  const active = !!activePackage;
  const expired = !active && (plan?.license || plan?.subscription);

  const alertLicense = (
    plan.license && plan.subscription &&
    plan.subscription.package >= plan.license.package
  );
  const alertSubscription = (
    (plan.subscription?.is_active && plan.subscription.is_terminal) ||
    (plan.subscription?.is_active && !plan.subscription.is_terminal && !payment)
  );

  // Handle navigation
  const onSubscribe = React.useCallback(() => {
    // Set subsequent steps to default
    store.package_store.setSelected(store.package_default);
    store.billing_store.setSelected(store.billing_default);
    store.payment_store.setSelected(store.payment_default);

    // Go to package modal
    modal.history.push(`${root}/header`);
  }, [store, modal, root]);

  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.name()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={styles.hero}>
              {active ? (
                <img className={styles.heroIcon} src={styles.heroIconImageStatusGood} alt='Good Icon' />
              ) : (
                <img className={styles.heroIcon} src={styles.heroIconImageStatusWarn} alt='Warning Icon' />
              )}
              <div className={styles.heroTitle}>
                {Messages.textStatus(activePackageName,
                                     active ? PlanFirewallMessages.statusActive() :
                                     expired ? PlanFirewallMessages.statusExpired() :
                                     PlanFirewallMessages.statusInactive())}
              </div>
              <div className={styles.heroDetail}>
                {active ? Messages.textThanks() : Messages.textPrompt()}
              </div>
            </div>
          </div>
          {active && (
            <div className={styles.section}>
              {plan.license?.package && (
                <Link className={classNames(styles.field, styles.item, styles.clickable)}
                      to={`${root}/status/license`}>
                  <div className={styles.fieldTextLeft} >
                    {Messages.fieldLicenseValue(getPlanFirewallPackageName(plan.license.package))}
                    {alertLicense && '*'}
                    <div className={styles.fieldInfo}>
                      {Messages.fieldLicenseLabel()}
                    </div>
                  </div>
                  <div className={styles.fieldAlert}>
                    {alertLicense && <div className={styles.alertCritical} />}
                    <img className={styles.fieldIcon} src={styles.fieldIconImageLicense} alt='License Icon' />
                  </div>
                  <div className={styles.open}/>
                </Link>
              )}
              {(plan.subscription?.package && plan.subscription?.is_active) && (
                <Link className={classNames(styles.field, styles.item, styles.clickable)}
                      to={`${root}/status/subscription`}>
                  <div className={styles.fieldTextLeft} >
                    {Messages.fieldSubscriptionValue(getPlanFirewallPackageName(plan.subscription.package),
                                                     plan.subscription.valid_until.toLocaleDateString(locale, format))}
                    {alertSubscription && '*'}
                    <div className={styles.fieldInfo}>
                      {Messages.fieldSubscriptionLabel()}
                    </div>
                  </div>
                  <div className={styles.fieldAlert}>
                    {alertSubscription && <div className={styles.alertCritical} />}
                    <img className={styles.fieldIcon} src={styles.fieldIconImageSubscription} alt='Subscription Icon' />
                  </div>
                  <div className={styles.open}/>
                </Link>
              )}
            </div>
          )}
          <div className={styles.section}>
            <Link className={styles.link} to={`${root}/status/invoices`}>
              {Messages.actionInvoiceList()}
            </Link>
          </div>
          {!active && (
            <ActiveButton onClick={onSubscribe}>
              {ManagePlansMessages.actionSubscribe()}
            </ActiveButton>
          )}
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type PlanItemFirewallStatusModalProps = PlanItemFirewallBaseRequiredModalProps;
export const PlanItemFirewallStatusModal = observer((props: PlanItemFirewallStatusModalProps) => {
  const { root, plan } = props;
  return (
    <Switch>
      <Route exact path={`${root}/status`}>
        <PlanItemFirewallStatusHomeModal {...props} />
      </Route>
      {plan.license && (
        <Route exact path={`${root}/status/license`}>
          <PlanItemFirewallStatusLicenseModal {...props}
            license={plan.license}
          />
        </Route>
      )}
      {plan.subscription && (
        <Route exact path={`${root}/status/subscription`}>
          <PlanItemFirewallStatusSubscriptionModal {...props}
            subscription={plan.subscription}
          />
        </Route>
      )}
      {plan.subscription && (
        <Route exact path={`${root}/status/subscription/cancel`}>
          <PlanItemFirewallStatusSubscriptionCancelModal {...props}
            subscription={plan.subscription}
          />
        </Route>
      )}
      <Route exact path={`${root}/status/invoices`}>
        <PlanItemFirewallStatusInvoiceListModal {...props} />
      </Route>
      <Route exact path={`${root}/status/invoices/:invoiceID`}>
        <PlanItemFirewallStatusInvoiceViewModal {...props} />
      </Route>
    </Switch>
  );
});
